/**
 * FilterPanel Component
 * Main container for all comparison filters
 */

import { useComparisonStore } from '@/store'
import AssetMultiSelect from './AssetMultiSelect'
import DateRangePicker from './DateRangePicker'
import KPISelector from './KPISelector'
import { Filter, RefreshCw, X } from 'lucide-react'
import clsx from 'clsx'

interface FilterPanelProps {
  className?: string
  layout?: 'horizontal' | 'vertical'
  showAssetSelector?: boolean
  showDatePicker?: boolean
  showKPISelector?: boolean
  onApply?: () => void
}

export default function FilterPanel({
  className,
  layout = 'vertical',
  showAssetSelector = true,
  showDatePicker = true,
  showKPISelector = true,
  onApply
}: FilterPanelProps) {
  const {
    selectedAssetIds,
    filters,
    setFilters,
    resetFilters,
    fetchKPIComparison,
    fetchTrendComparison,
    loading
  } = useComparisonStore()

  const handleDateChange = (dateRange: typeof filters.dateRange) => {
    setFilters({ dateRange })
  }

  const handleKPIChange = (kpis: string[]) => {
    setFilters({ kpis })
  }

  const handleApply = async () => {
    await Promise.all([
      fetchKPIComparison(),
      fetchTrendComparison()
    ])
    onApply?.()
  }

  const handleReset = () => {
    resetFilters()
  }

  const hasFilters = selectedAssetIds.length > 0 || filters.kpis.length > 0

  return (
    <div className={clsx(
      'bg-surface-900/50 rounded-lg border border-surface-700 p-4',
      className
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-primary-400" />
          <h2 className="text-sm font-semibold text-surface-200">Filters</h2>
        </div>
        {hasFilters && (
          <button
            onClick={handleReset}
            className="text-xs text-surface-400 hover:text-surface-200 flex items-center gap-1"
          >
            <X size={12} />
            Reset
          </button>
        )}
      </div>

      {/* Filter Controls */}
      <div className={clsx(
        'space-y-4',
        layout === 'horizontal' && 'lg:flex lg:gap-4 lg:space-y-0'
      )}>
        {/* Asset Selection */}
        {showAssetSelector && (
          <div className={layout === 'horizontal' ? 'lg:flex-1' : ''}>
            <AssetMultiSelect maxSelection={5} />
          </div>
        )}

        {/* Date Range */}
        {showDatePicker && (
          <div className={layout === 'horizontal' ? 'lg:w-64' : ''}>
            <label className="block text-xs text-surface-400 mb-1.5">Time Period</label>
            <DateRangePicker
              value={filters.dateRange}
              onChange={handleDateChange}
            />
          </div>
        )}

        {/* KPI Selection */}
        {showKPISelector && (
          <div className={layout === 'horizontal' ? 'lg:flex-1' : ''}>
            <KPISelector
              selectedKPIs={filters.kpis}
              onChange={handleKPIChange}
            />
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-surface-700">
        <div className="text-xs text-surface-400">
          {selectedAssetIds.length > 0 ? (
            <span>
              {selectedAssetIds.length} asset{selectedAssetIds.length !== 1 ? 's' : ''} selected
              {filters.kpis.length > 0 && `, ${filters.kpis.length} KPIs`}
            </span>
          ) : (
            <span>Select assets to compare</span>
          )}
        </div>

        <button
          onClick={handleApply}
          disabled={loading || selectedAssetIds.length === 0}
          className={clsx(
            'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
            selectedAssetIds.length > 0
              ? 'bg-primary-500 hover:bg-primary-600 text-white'
              : 'bg-surface-700 text-surface-400 cursor-not-allowed'
          )}
        >
          {loading ? (
            <>
              <RefreshCw size={14} className="animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <RefreshCw size={14} />
              Apply Filters
            </>
          )}
        </button>
      </div>
    </div>
  )
}
